<script setup lang="ts">
import { AdditiveItem } from '@/types/api/api-v3/basket'

import BasketAdditiveItem from '@/components/Basket/basket-additive/BasketAdditiveItem.vue'
import BasketAdditiveToggle from '@/components/Basket/basket-additive/BasketAdditiveToggle.vue'
import BasketTitle from '@/components/Basket/basket-utils/BasketTitle.vue'

const DialogBasketAdditives = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketAdditives.vue'),
)

const MAX_VISIBLE_ADDITIVES_FOR_DESKTOP = 4
const MAX_VISIBLE_ADDITIVES_FOR_MOBILE = 3

type Props = {
  additives: AdditiveItem[]
}

const props = defineProps<Props>()

const { isMobile } = useDevice()

const showAllAdditives = ref(false)
const maxVisibleAdditives = ref(
  isMobile
    ? MAX_VISIBLE_ADDITIVES_FOR_MOBILE
    : MAX_VISIBLE_ADDITIVES_FOR_DESKTOP,
)

const dialogs = ref({
  allAdditives: {
    visible: false,
  },
})

const showAllButtonVisible = computed(() => {
  if (isMobile) {
    return (
      props.additives.length > maxVisibleAdditives.value &&
      props?.additives?.length > MAX_VISIBLE_ADDITIVES_FOR_MOBILE
    )
  }

  return (
    props.additives.length > maxVisibleAdditives.value &&
    props?.additives?.length > MAX_VISIBLE_ADDITIVES_FOR_DESKTOP
  )
})

const countOfVisibleAdditives = computed(() =>
  showAllAdditives.value ? props.additives.length : maxVisibleAdditives.value,
)

const toggleShowAllAdditives = () => {
  if (isMobile) {
    dialogs.value.allAdditives.visible = true

    return
  }

  showAllAdditives.value = !showAllAdditives.value
}
</script>

<template>
  <div class="additives">
    <div class="additives__list">
      <basket-additive-item
        v-for="(additive, index) of additives"
        v-show="index + 1 <= countOfVisibleAdditives"
        :key="additive.product_id"
        :additive="additive"
        :additives="additives"
        class="additives__list-item"
      />
    </div>

    <basket-additive-toggle
      v-if="showAllButtonVisible"
      :show-all-additives="showAllAdditives"
      @toggle-additives="toggleShowAllAdditives"
    />

    <the-bottom-sheet v-model="dialogs.allAdditives.visible">
      <basket-title class="dialog-title" title="Добавить к заказу" />

      <dialog-basket-additives :additives="additives" />
    </the-bottom-sheet>
  </div>
</template>

<style lang="scss" scoped>
.additives {
  &__list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 0 10px;
    margin-bottom: 15px;

    @include media('>=tablet') {
      grid-template-columns: repeat(4, 1fr);
      gap: 0 16px;
      justify-content: space-between;
      margin-bottom: 16px;
    }

    @include media('>=desktop-mini') {
      gap: 25px 16px;
    }
  }
}

.dialog-title {
  position: static !important;
  padding: 0 !important;
  margin-bottom: 0;
}
</style>
